import { FormikProps } from "formik";
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  ModalFooter,
  useDisclosure,
} from "@chakra-ui/react";
import { FormValues } from "@/models/proposalForm.model";
import { MdAccountBalance } from "react-icons/md";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { PixPaymentFields } from "./PixPaymentFields";
import BankPaymentFields from "./BankPaymentField";
import { Proposal } from "@/models/proposal.model";
import { useForm } from "@/contexts/proposalFormContext";
import { useEffect, useState } from "react";
import { emailChecker } from "@/utils/EmailChecker";
import { validateCPF } from "@/utils/validateCPF";
import { phoneValidator } from "@/utils/phoneValidator";
import { bankAccountValidator } from "@/utils/bankAccountValidator";
import { PixTypeEnum } from "@/models/pixType.model";

interface BankDataStepProps {
  formik: FormikProps<FormValues["bankData"]>;
  handleBack: () => void;
  proposal?: Proposal | null;
}

export default function BankDataStep({
  formik,
  handleBack,
  proposal,
}: BankDataStepProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalStep, setModalStep] = useState<0 | 1>(0);
  const [countdown, setCountdown] = useState<number>(5);
  const modalStepsContent = {
    0: {
      title: "Atenção",
      description: [
        "Evite pendências em sua proposta.",
        "O valor só será enviado na conta de quem está realizando a operação.",
      ],
    },
    1: {
      title: "A chave Pix cadastrada é de sua titularidade?",
      description: [],
    },
  };
  const [isPixKeyValid, setIsPixKeyValid] = useState<boolean>(false);
  const { formData } = useForm();
  const { personalData } = formData;

  useEffect(() => {
    const { pixType, pixKey } = formik.values;

    let isValid = false;

    switch (pixType) {
      case PixTypeEnum.EMAIL:
        isValid = !!emailChecker(pixKey!);
        break;
      case PixTypeEnum.DOCUMENT:
        isValid = validateCPF(pixKey!);
        break;
      case PixTypeEnum.PHONE:
        isValid = phoneValidator(pixKey!);
        break;
      case PixTypeEnum.BANK_ACCOUNT:
        isValid = bankAccountValidator(formik?.values);
        break;
      default:
        break;
    }

    setIsPixKeyValid(isValid);
  }, [
    formik.values.pixKey,
    formik.values.pixType,
    formik.values.bankCode,
    formik.values.agency,
    formik.values.account,
    formik.values.digit,
    formik.values.accountType,
  ]);

  useEffect(() => {
    onOpen();
  }, []);

  const handleModalNext = () => {
    if (modalStep === 0) {
      setModalStep(1);
      onClose();
    }
    if (modalStep === 1) {
      formik.submitForm();
      setModalStep(0);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setCountdown(5);
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev === 1) clearInterval(interval);
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  return (
    <Box
      bg="white"
      p={6}
      borderRadius="25px"
      boxShadow="md"
      borderColor="gray.200"
      maxW="800px"
      mx="auto"
      mt={10}
    >
      <Flex align="center" mb={4}>
        <Icon as={MdAccountBalance} boxSize={8} color="gray.500" mr={2} />
        <Box>
          <Heading size="md" color="gray.700" fontFamily="Lexend Deca">
            Selecione Sua Chave Pix
          </Heading>
        </Box>
      </Flex>
      <Divider mb={6} />

      <PixPaymentFields
        formik={formik}
        proposal={proposal}
        personalData={personalData}
        isPixKeyValid={isPixKeyValid}
      />

      {formik.values.pixType === "BANK_ACCOUNT" && <BankPaymentFields formik={formik} />}

      <Flex justifyContent="flex-end" gap={3} mt={1}>
        <Button
          leftIcon={<Icon as={IoIosArrowBack} />}
          colorScheme="secondary"
          variant="outline"
          onClick={handleBack}
          fontFamily="Lexend Deca"
        >
          Voltar
        </Button>
        <Button
          rightIcon={<Icon as={IoIosArrowForward} />}
          colorScheme="primary"
          onClick={onOpen}
          fontFamily="Lexend Deca"
          isDisabled={isPixKeyValid ? false : true}
        >
          Prosseguir
        </Button>
      </Flex>

      {/* Modal */}

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xs">
        <ModalOverlay />
        <ModalContent
          borderRadius="2xl"
          px={4}
          py={6}
          bg="white"
          fontFamily="Lexend Deca"
          boxShadow="lg"
        >
          <ModalHeader
            fontSize="lg"
            textAlign="center"
            color="gray.700"
            px={0}
            pt={0}
            mt={8}
          >
            {modalStep === 0 && (
              <Box
                background="#E53E3E"
                borderRadius={10}
                width="50%"
                mx="auto"
                py={2}
                fontWeight={"normal"}
              >
                <Text color="white" fontFamily="Lexend Deca">
                  {modalStepsContent[modalStep]?.title}
                </Text>
              </Box>
            )}
            {modalStep !== 0 && (
              <Text fontFamily="Lexend Deca">{modalStepsContent[modalStep]?.title}</Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          {modalStepsContent[modalStep]?.description.length > 0 && (
            <ModalBody
              pt={2}
              mx={5}
              border="1px"
              borderRadius={20}
              borderColor="gray.500"
            >
              {modalStepsContent[modalStep]?.description.map((text) => (
                <Text
                  key={text}
                  fontSize="md"
                  textAlign="center"
                  color="gray.600"
                  fontFamily="Lexend Deca"
                >
                  {text}
                </Text>
              ))}
            </ModalBody>
          )}
          <ModalFooter
            display="flex"
            justifyContent="center"
            gap={3}
            flexDirection="row"
            px={0}
            pt={6}
          >
            {modalStep === 1 && (
              <Button
                bg="#E53E3E"
                color="white"
                _hover={{ bg: "#F56565" }}
                onClick={onClose}
                borderRadius="lg"
                fontSize="sm"
                px={6}
                py={5}
                fontWeight={500}
                fontFamily="Lexend Deca"

              >
                Não, vou cadastrar.
              </Button>
            )}

            <Button
              bg="#48BB78"
              color="white"
              _hover={{ bg: "#6CC893" }}
              onClick={handleModalNext}
              isDisabled={countdown > 0 && modalStep === 1}
              borderRadius="lg"
              fontSize="sm"
              px={6}
              py={5}
              fontWeight={500}
              fontFamily="Lexend Deca"
            >
              {modalStep === 0
                ? "Clique e Cadastre sua Chave." 
                : countdown > 0
                ? `Sim, é minha. (${countdown})`
                : "Sim, é minha."}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
